import { Looker40SDK } from '@looker/sdk';

import { query } from '@/api/looker/utils/query';
import { IWarrantyRepository } from '@/warranty/domain/repositories/warranty.repository';

import { type BatteryLevel, DIM_BY_LEVEL } from '../constants/battery-levels';

import {
  type ConditionViolationRow,
  type ConditionViolationTimeseriesRow,
  transformConditionViolationStatus,
  transformConditionViolationTimeseriesData,
  transformConditionViolationTimeseriesRow,
} from './mappers/condition-violations.repository.mapper';
import {
  type LevelRow,
  transformLevelRow,
} from './mappers/entity-lists.repository.mapper';
import {
  type PerformanceWarrantyStateRow,
  type RawWarrantyCaseAlertsRow,
  transformPerformanceWarrantyState,
} from './mappers/gauge-metrics.repository.mapper';
// Penalty chart mapper
import {
  type PenaltyChartRow,
  transformPenaltyChartRows,
} from './mappers/penalty-chart.repository.mapper';
import {
  type PenaltyOverviewRow,
  transformPenaltyOverview,
} from './mappers/penalty.repository.mapper';
import { buildIdFilters, getAggregationLevel } from './mappers/utils';
// Import mappers
import {
  applyCustomerSpecificTransformations,
  getWarrantyStatusQueryFields,
  transformWarrantyStatusResult,
  type WarrantyIDStatusRow,
} from './mappers/warranty-id-status.repository.mapper';

export class LookerWarrantyRepository implements IWarrantyRepository {
  constructor(
    private coreSDK: Looker40SDK,
    private lookerModel: string,
  ) {}

  get key() {
    return this.lookerModel;
  }

  private readonly levelSpecificFields: Record<
    BatteryLevel | 'site',
    string[]
  > = {
    site: [DIM_BY_LEVEL.site],
    array: [DIM_BY_LEVEL.array],
    node: [DIM_BY_LEVEL.node],
    container: [DIM_BY_LEVEL.container],
    bank: [DIM_BY_LEVEL.bank],
    pack: [DIM_BY_LEVEL.pack],
    module: [DIM_BY_LEVEL.module],
    cell: [DIM_BY_LEVEL.cell],
  };

  // --- overview table ---
  async getAllWarrantyIDStatus(
    input: IWarrantyRepository.GetAllWarrantyIDStatusInput,
  ): Promise<IWarrantyRepository.GetAllWarrantyIDStatusOutput> {
    // Get customer ID from input instead of using hook
    const customerId = input.customerId;

    const aggregationLevel = getAggregationLevel(
      input.filters,
      customerId,
      input.assetId,
    );

    // this is the query that is used to get the warranty status for the overview table
    const queryFields = getWarrantyStatusQueryFields(aggregationLevel);

    let queryLevel = undefined;
    if (customerId === '69') {
      if (aggregationLevel === 'array') {
        queryLevel = 'array,container';
      } else if (aggregationLevel === 'container') {
        queryLevel = 'container';
      }
    } else if (customerId === '75') {
      if (aggregationLevel === 'array') {
        queryLevel = 'array';
      }
      if (aggregationLevel === 'container') {
        queryLevel = 'container';
      }
    }

    const queryFilters = buildIdFilters(
      input.assetFullFrontendId,
      input.filters,
      queryLevel,
    );

    const data = await query(this.coreSDK)
      .from<WarrantyIDStatusRow>({
        model: this.lookerModel,
        view: 'platform_core_warranty_unit_status_all_levels',
        fields: queryFields,
        filters: queryFilters,
        sorts: [...this.levelSpecificFields[aggregationLevel]],
      })
      .toMany((row) => row);

    // First transform the raw data
    const baseTransformedData = data.map((row) =>
      transformWarrantyStatusResult(row, aggregationLevel),
    );

    // Then apply customer-specific transformations if customer ID is provided
    const transformedData = customerId
      ? baseTransformedData.map((item) =>
          applyCustomerSpecificTransformations(
            item,
            customerId,
            aggregationLevel,
          ),
        )
      : baseTransformedData;

    return transformedData;
  }

  // --- gauges ---
  async getPerformanceWarrantyStates(
    input: IWarrantyRepository.GetPerformanceWarrantyStatesInput,
  ): Promise<IWarrantyRepository.GetPerformanceWarrantyStatesOutput> {
    const customerId = input.customerId;

    // This is ugly! Fix it!
    const tagDescriptionId =
      customerId === '69' ? 'AVAILABILITY_PCS' : 'AVAILABILITY';

    const sohQueryFilters: Record<string, string> = {
      'frontend_id_mapping.full_frontend_system_id': input.assetFullFrontendId,
      ...(customerId === '69' || customerId === '75'
        ? { 'frontend_id_mapping.accure_level': 'system' }
        : { 'frontend_id_mapping.is_unit_level': 'true' }),
    };

    const tagQueryFilters = {
      'platform_xfleet_warranty_case_alerts.description_id': tagDescriptionId,
      'frontend_id_mapping.full_frontend_system_id': input.assetFullFrontendId
        ? input.assetFullFrontendId
        : '',
    };

    const rteQueryFilters = {
      'platform_xfleet_warranty_case_alerts.description_id': 'RTE',
      'frontend_id_mapping.full_frontend_system_id': input.assetFullFrontendId
        ? input.assetFullFrontendId
        : '',
    };

    const [sohData, tagData, rteData] = await Promise.all([
      // sohData
      query(this.coreSDK)
        .from<PerformanceWarrantyStateRow>({
          model: this.lookerModel,
          view: 'platform_core_warranty_unit_status',
          fields: [
            'platform_core_warranty_unit_status.soh_accure_agg',
            'platform_core_warranty_unit_status.soh_warranty_avg',
          ],
          filters: sohQueryFilters,
          sorts: [],
        })
        .toFirst((row) => row),
      // tagData
      query(this.coreSDK)
        .from<RawWarrantyCaseAlertsRow>({
          model: this.lookerModel,
          view: 'platform_xfleet_warranty_case_alerts',
          fields: [
            'platform_xfleet_warranty_case_alerts.value_avg',
            'platform_xfleet_warranty_case_alerts.threshold_avg',
            'platform_xfleet_warranty_case_alerts.window_end_date',
          ],
          filters: tagQueryFilters,
          sorts: ['platform_xfleet_warranty_case_alerts.window_end_date desc'],
        })
        .toFirstOrUndefined((row) => row),
      // rteData
      query(this.coreSDK)
        .from<RawWarrantyCaseAlertsRow>({
          model: this.lookerModel,
          view: 'platform_xfleet_warranty_case_alerts',
          fields: [
            'platform_xfleet_warranty_case_alerts.value_avg',
            'platform_xfleet_warranty_case_alerts.threshold_avg',
            'platform_xfleet_warranty_case_alerts.window_end_date',
          ],
          filters: rteQueryFilters,
          sorts: ['platform_xfleet_warranty_case_alerts.window_end_date desc'],
        })
        .toFirstOrUndefined((row) => row),
    ]);

    return transformPerformanceWarrantyState(
      {
        ...sohData,
        'tag.value_avg':
          tagData?.['platform_xfleet_warranty_case_alerts.value_avg'],
        'tag.threshold_avg':
          tagData?.['platform_xfleet_warranty_case_alerts.threshold_avg'],
        'rte.value_avg':
          rteData?.['platform_xfleet_warranty_case_alerts.value_avg'],
        'rte.threshold_avg':
          rteData?.['platform_xfleet_warranty_case_alerts.threshold_avg'],
      },
      customerId,
    );
  }

  // --- penalty overview ---
  async getWarrantyPenaltyChartData(
    input: IWarrantyRepository.GetWarrantyPenaltyChartDataInput,
  ): Promise<IWarrantyRepository.GetWarrantyPenaltyChartDataOutput> {
    let levelFilter = undefined;
    let level = getAggregationLevel(
      input.filters,
      input.customerId,
      input.assetId,
    );

    // Conditional level filtering based on customer ID
    if (input.customerId === '69') {
      levelFilter = 'site';
      level = 'site';
    } else if (input.customerId === '75') {
      if (input.assetId === '26254085168192602') {
        levelFilter = 'array';
        level = 'array';
      } else {
        levelFilter = 'container';
        level = 'container';
      }
    }
    const queryFilters = buildIdFilters(
      input.assetFullFrontendId,
      input.filters,
      levelFilter,
    );
    const levelFields = this.levelSpecificFields[level];

    const queryFields = [
      // level specific id fields (ensure uniqueness)
      ...levelFields,
      // always include site identifier for grouping context
      DIM_BY_LEVEL.site,
      // data fields
      'platform_core_warranty_penalty_unit_alerts.description_id',
      'platform_core_warranty_penalty_unit_alerts.penalty_historical_agg',
      'platform_core_warranty_penalty_unit_alerts.penalty_future_agg',
      'platform_core_warranty_penalty_unit_alerts.last_detection_future',
    ];

    // execute Looker query
    const rows = await query(this.coreSDK)
      .from<PenaltyChartRow>({
        model: this.lookerModel,
        view: 'platform_core_warranty_penalty_unit_alerts',
        fields: queryFields,
        filters: queryFilters,
        sorts: [],
      })
      .toMany((result) => result as PenaltyChartRow);

    return transformPenaltyChartRows(rows, level);
  }

  async getPenaltyOverview(
    input: IWarrantyRepository.GetPenaltyOverviewInput,
  ): Promise<IWarrantyRepository.GetPenaltyOverviewOutput> {
    // Conditional level filtering based on customer ID
    const levelFilter =
      input.customerId === '69'
        ? 'site'
        : input.customerId === '75'
          ? input.assetId === '26254085168192602'
            ? 'array'
            : 'container'
          : undefined;

    const queryFilters = buildIdFilters(
      input.assetFullFrontendId,
      input.filters,
      levelFilter,
    );

    return query(this.coreSDK)
      .from<PenaltyOverviewRow>({
        model: this.lookerModel,
        view: 'platform_core_warranty_penalty_unit_alerts',
        fields: [
          'platform_core_warranty_penalty_unit_alerts.description_id',
          'platform_core_warranty_penalty_unit_alerts.extreme_value_agg',
          'platform_core_warranty_penalty_unit_alerts.value_unit',
          'platform_core_warranty_penalty_unit_alerts.last_detection_historical',
          'platform_core_warranty_penalty_unit_alerts.duration_h_historical_agg',
          'platform_core_warranty_penalty_unit_alerts.count_historical_agg',
          'platform_core_warranty_penalty_unit_alerts.penalty_historical_agg',
        ],
        filters: queryFilters,
        sorts: [],
      })
      .toMany(transformPenaltyOverview);
  }

  // --- violations section ---
  async getConditionViolationTimeseriesData(
    input: IWarrantyRepository.GetConditionViolationTimeseriesDataInput,
  ): Promise<IWarrantyRepository.GetConditionViolationTimeseriesDataOutput> {
    // Conditional level filtering based on customer ID
    const levelFilter =
      input.customerId === '69'
        ? 'system'
        : input.customerId === '75'
          ? input.assetId === '26254085168192602'
            ? 'array'
            : 'container'
          : undefined;

    const queryFilters = buildIdFilters(
      input.assetFullFrontendId,
      input.filters,
      levelFilter,
    );

    const data = await query(this.coreSDK)
      .from<ConditionViolationTimeseriesRow>({
        model: this.lookerModel,
        view: 'platform_core_warranty_condition_unit_alerts',
        fields: [
          'platform_core_warranty_condition_unit_alerts.description_id',
          'platform_core_warranty_condition_unit_alerts.analysis_date',
          'platform_core_warranty_condition_unit_alerts.count_agg',
        ],
        filters: queryFilters,
        sorts: [],
      })
      .toMany(transformConditionViolationTimeseriesRow);

    return transformConditionViolationTimeseriesData(data);
  }

  // condition violations overview table
  async getConditionViolationsStatus(
    input: IWarrantyRepository.GetConditionViolationStatusInput,
  ): Promise<IWarrantyRepository.GetConditionViolationStatusOutput> {
    // Conditional level filtering based on customer ID
    const levelFilter =
      input.customerId === '69'
        ? 'system'
        : input.customerId === '75'
          ? input.assetId === '26254085168192602'
            ? 'array'
            : 'container'
          : undefined;

    const queryFilters = buildIdFilters(
      input.assetFullFrontendId,
      input.filters,
      levelFilter,
    );

    return query(this.coreSDK)
      .from<ConditionViolationRow>({
        model: this.lookerModel,
        view: 'platform_core_warranty_condition_unit_alerts',
        fields: [
          'platform_core_warranty_condition_unit_alerts.description_id',
          'platform_core_warranty_condition_unit_alerts.extreme_value_agg',
          'platform_core_warranty_condition_unit_alerts.value_unit',
          'platform_core_warranty_condition_unit_alerts.last_detection',
          'platform_core_warranty_condition_unit_alerts.duration_h_agg',
          'platform_core_warranty_condition_unit_alerts.count_agg',
        ],
        filters: queryFilters,
        sorts: [],
      })
      .toMany(transformConditionViolationStatus);
  }

  // --- Penalties details section ---
  // NOTE: Currently not shown!
  async getPenaltyDetails(
    _: IWarrantyRepository.GetPenaltyDetailsInput,
  ): Promise<IWarrantyRepository.GetPenaltyDetailsOutput> {
    return [];
  }

  // NOTE: Currently not shown!
  async getPenaltyTimeseriesData(
    _: IWarrantyRepository.PenaltyTimeseriesDataInput,
  ): Promise<IWarrantyRepository.PenaltyTimeseriesDataOutput> {
    return [];
  }

  // --- helper ---
  async listSites(): Promise<IWarrantyRepository.ListSitesOutput> {
    return { sites: [] };
  }

  async listLevels(): Promise<IWarrantyRepository.ListLevelsOutput> {
    const levels = await query(this.coreSDK)
      .from<LevelRow>({
        model: this.lookerModel,
        view: 'onboarding_level_info',
        fields: [
          'onboarding_level_info.accure_level',
          'onboarding_level_info.customer_level_name',
        ],
        // filters: queryFilters,
        sorts: ['onboarding_level_info.accure_level'],
      })
      .toMany(transformLevelRow);
    return { levels };
  }

  // --- generic entity list ---
  async listEntities(
    input: IWarrantyRepository.ListEntitiesInput,
  ): Promise<IWarrantyRepository.ListEntitiesOutput> {
    const { level, assetFullFrontendId, filters } = input;

    const queryFilters: Record<string, string> = {
      'frontend_id_mapping.full_frontend_system_id': assetFullFrontendId
        ? assetFullFrontendId
        : '',
      'frontend_id_mapping.accure_level': level,
    };

    // Map of battery level -> Looker dimension for its full frontend id
    const levelToField: Record<IWarrantyRepository.BatteryLevel, string> = {
      array: 'frontend_id_mapping.full_frontend_array_id',
      node: 'frontend_id_mapping.full_frontend_node_id',
      container: 'frontend_id_mapping.full_frontend_container_id',
      bank: 'frontend_id_mapping.full_frontend_bank_id',
      pack: 'frontend_id_mapping.full_frontend_pack_id',
      module: 'frontend_id_mapping.full_frontend_module_id',
      cell: 'frontend_id_mapping.full_frontend_cell_id',
    } as const;

    // Apply parent-level filters so selectors are dependent.
    const hierarchy: IWarrantyRepository.BatteryLevel[] = [
      'array',
      'node',
      'container',
      'bank',
      'pack',
      'module',
      'cell',
    ];

    const currentIdx = hierarchy.indexOf(level);
    if (currentIdx > 0) {
      for (let i = 0; i < currentIdx; i++) {
        const parent = hierarchy[i];
        const filterKey = `${parent}Id` as const;
        const filterValue = (filters as any)[filterKey] as string | undefined;
        if (filterValue) {
          const lookerField = levelToField[parent];
          queryFilters[lookerField] = filterValue;
        }
      }
    }

    const entities = await query(this.coreSDK)
      .from<{
        'frontend_id_mapping.full_frontend_id': string;
        'frontend_id_mapping.accure_id': string;
      }>({
        model: this.lookerModel,
        view: 'frontend_id_mapping',
        fields: [
          'frontend_id_mapping.full_frontend_id',
          'frontend_id_mapping.accure_id',
        ],
        filters: queryFilters,
        sorts: ['frontend_id_mapping.full_frontend_id asc'],
      })
      .toMany<IWarrantyRepository.Entity>((row) => ({
        id: row['frontend_id_mapping.accure_id'],
        fullFrontendId: row['frontend_id_mapping.full_frontend_id'],
      }));

    return { entities };
  }
}
